// The SWRV fetcher's logic (stale-while-revalidate over the offline HTTP cache),
// with its dependencies passed in. No `@/` import, so `node --test` can load
// this file directly; fetcher.js wires it to axios, SQLite and SWRV.
//
// deps: { get(key) → axios response, readCache(key) → {data}|null,
//         writeCache(key, data), removeCache(key), refuse(key), mutate(key, value),
//         isOffline() → boolean, isNetworkError(error) → boolean, warn(...args) }

// A 403 means the reader lost access (e.g. a session no longer shared with
// them). Their cached copy must not be shown any more, nor kept (S5).
export const isAccessDenied = (error) => error?.response?.status === 403

export function createFetcher(deps) {
    const { get, readCache, writeCache, removeCache, refuse, mutate, isOffline, isNetworkError, warn } = deps

    // Both copies go: the offline one, and SWRV's in-memory one, which a page
    // opened again would otherwise show without asking.
    async function dropRefusedCopies(key) {
        await removeCache(key)
        await refuse(key)
    }

    async function fetchFromNetwork(key, formatter, { cache = true } = {}) {
        const response = await get(key)
        const resolved = formatter ? formatter(response.data) : response.data

        if (cache) {
            writeCache(key, resolved) // fire-and-forget — don't delay the response for cache write
        }

        return resolved
    }

    async function refreshInBackground(key, formatter) {
        if (isOffline()) {
            return
        }
        try {
            const fresh = await fetchFromNetwork(key, formatter, { cache: true })
            await mutate(key, fresh)
        } catch (error) {
            if (isNetworkError(error)) {
                return
            }
            if (isAccessDenied(error)) {
                // The copy just served is no longer the reader's to see: drop it
                // and hand SWRV the refusal instead.
                await dropRefusedCopies(key)
                await mutate(key, Promise.reject(error))
                return
            }
            warn('[Fetcher] Background refresh failed', key, error)
        }
    }

    // The network failed: fall back on the cached copy, except after a refusal.
    async function afterNetworkError(key, error) {
        if (isAccessDenied(error)) {
            await dropRefusedCopies(key)
            throw error
        }

        const networkLikeError = isNetworkError(error)
        const cacheEntry = await readCache(key)
        if (cacheEntry) {
            if (!networkLikeError) {
                warn('[Fetcher] Serving cached data due to request error', key, error)
            }
            return cacheEntry.data
        }

        if (networkLikeError || error?.isOffline || isOffline()) {
            return null
        }

        throw error
    }

    return async function fetcher(key, formatter, options = {}) {
        const {
            forceNetwork = false,
            disableCache = false,
            backgroundRefresh = true,
            cacheOnly = false,
        } = options || {}
        const offline = isOffline()

        if (disableCache) {
            return offline && !forceNetwork ? null : fetchFromNetwork(key, formatter, { cache: false })
        }

        if (!forceNetwork) {
            const cacheEntry = await readCache(key)
            if (cacheEntry) {
                if (!offline && backgroundRefresh) {
                    refreshInBackground(key, formatter)
                }
                return cacheEntry.data
            }
            if (cacheOnly || offline) {
                return null
            }
        }

        try {
            return await fetchFromNetwork(key, formatter, { cache: true })
        } catch (error) {
            return afterNetworkError(key, error)
        }
    }
}
